import { ChangeEvent, FunctionComponent, KeyboardEvent } from 'react';
import kebabCase from 'lodash/kebabCase';
import { ICON_NAMES, ITestable } from '@belong/types';
import { KEYBOARD_KEYS } from '../../../helpers/variables';
import { Icon } from '../../Icon/Icon';
import { HiddenInput, LabelChip } from './ChoiceChip.styles';

export const TEST_ID = 'ChoiceChip';

export interface IChoiceChipProps extends ITestable {
  'aria-describedby'?: string;
  label: string;
  name: string;
  value: string;
  inputType?: 'checkbox' | 'radio';
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  isError?: boolean;
  isSelected?: boolean;
}

/**
 * Single, selectable, rounded choice button
 */
const ChoiceChip: FunctionComponent<IChoiceChipProps> = ({
  label,
  value,
  isSelected = false,
  isError = false,
  onChange,
  inputType = 'checkbox',
  name,
  'aria-describedby': ariaDescribedBy,
  ...otherProps
}) => {
  const id = kebabCase(`${name}-${value}`);

  // allow checkbox interaction via enter key
  const keyboardHandler = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (inputType === 'checkbox' && event.key === KEYBOARD_KEYS.ENTER) {
      event.currentTarget.click();
    }
  };

  const changeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
    if (onChange) {
      onChange(event);
    }
  };

  return (
    <LabelChip {...otherProps} htmlFor={id} isError={isError} isSelected={isSelected}>
      <HiddenInput
        aria-describedby={ariaDescribedBy}
        id={id}
        type={inputType}
        name={name}
        value={value}
        checked={isSelected}
        onChange={changeHandler}
        onKeyDown={keyboardHandler}
        // Prevents browsers restoring a stale "checked" value on back/forward navigation,
        // which can make the DOM out of sync from React's state and silently break the next click
        autoComplete="off"
      />
      {isSelected && <Icon name={ICON_NAMES.Tick} />}
      {label}
    </LabelChip>
  );
};

ChoiceChip.displayName = 'ChoiceChip';

export default ChoiceChip;
